import { useEffect, useRef, useState } from "react"

import { Flex, Tooltip, Typography } from "antd"
import cls from "classname"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useParams } from "react-router"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../Components/Button"
import TitleSection from "../../Components/TitleSection/TitleSection"
import { RoutesPaths } from "../../constants/user-routes"
import Icon from "../../Icon"
import { navActions } from "../../Redux/nav/nav.slice"
import { highAccessRoles } from "../../Utils/permission"
import { buildTestId } from "../../Utils/testId"
import BalanceActions from "../Balance/reducer"
import Balance from "../BusinessSettings/Balance"
import Billing from "../BusinessSettings/Billing"
import { logoutThunk } from "../LoginPage/redux/auth.thunks"
import Referrals from "../Referrals"
import { useIsReferralEnabled } from "../Referrals/hooks/useIsReferralEnabled"
import TeamPage from "../TeamPage"
import { userSetProp } from "../User/redux/user.actions"
import {
  confirmEmails,
  getBusinessAccount,
  getUserInfo,
  getUserInfoByAdmin
} from "../User/redux/user.thunks"
import Appearance from "./Appearance"
import BusinessAccounts from "./BusinessAccounts"
import { useBalanceTopUpEvent } from "./hooks/useBalanceTopUpEvent"
import McpIntegrations from "./Mcp"
import MyProfile from "./MyProfile"
import Notifications from "./Notifications"
import PublicAPI from "./PublicAPI/PublicAPI"
import Users from "./Users"

import "./styles.less"

const { Title, Text } = Typography

// Sidebar sections, rendered in this order with a 12px section title each.
const SECTION_ORDER = ["Personal", "Finance", "Other"]

const getTabsList = ({ referralEnabled, isBalanceTabAvailable, teamEnabled }) => [
  {
    id: "my_profile",
    text: "My profile",
    icon: "user",
    section: "Personal"
  },
  {
    id: "appearance",
    text: "Appearance",
    icon: "color_palette",
    section: "Personal"
  },
  {
    id: "notifications",
    text: "Notifications",
    icon: "notification",
    section: "Personal"
  },
  {
    id: "business_accounts",
    text: "Business accounts",
    icon: "bullet_list",
    section: "Personal"
  },
  // Team and Developers moved out of the sidebar into Settings (CHA1-2847).
  // Team keeps the role gate the standalone /team page had.
  ...(teamEnabled
    ? [
        {
          id: "team",
          text: "Team",
          icon: "team",
          section: "Personal"
        }
      ]
    : []),
  {
    id: "billing",
    text: "Billing",
    icon: "bank",
    checkFeature: true,
    section: "Finance"
  },
  ...(isBalanceTabAvailable
    ? [
        {
          id: "balance",
          text: "Balance",
          icon: "card",
          checkPermission: true,
          checkFeature: true,
          section: "Finance"
        }
      ]
    : []),
  ...(referralEnabled
    ? [
        {
          id: "referrals",
          text: "Referrals",
          icon: "referrals",
          hot: true,
          section: "Other"
        }
      ]
    : []),
  {
    id: "mcp",
    text: "MCP",
    icon: "plugin_connect",
    new: true,
    section: "Other"
  },
  {
    id: "developers",
    text: "Developers",
    icon: "developer",
    section: "Other"
  }
  // {
  //   id: 'public_api',
  //   text: 'Public API',
  //   icon: 'public_api',
  //   new: true,
  // },
  // {
  //   id: "referrals",
  //   text: "Referrals",
  //   icon: "referrals",
  //   new: true,
  // },
  // {
  //   id: "users",
  //   text: "Users",
  //   icon: "two_users",
  //   disabled: true,
  //   tooltip:
  //     "This action is restricted by your User Management settings for the current business account.",
  // },
]

const tabsListAdmin = [
  {
    id: "balance",
    text: "Balance",
    icon: "card",
    section: "Finance"
  },
  {
    id: "billing",
    text: "Billing",
    icon: "bank",
    section: "Finance"
  },
  {
    id: "business_accounts",
    text: "Business accounts",
    icon: "bullet_list",
    section: "Personal"
  },
  {
    id: "notifications",
    text: "Notifications",
    icon: "notification",
    section: "Personal"
  },
  {
    id: "public_api",
    text: "Public API",
    icon: "public_api",
    new: true,
    section: "Other"
  }
]

export const PersonalSettingsPage = ({
  confirmEmail,
  userInfo,
  saveResult,
  businessAccounts,
  balanceEnabled,
  isMobile,
  isShowMenu,
  isAdmin,
  bannerInfo,
  ...props
}) => {
  const { t } = useTranslation()

  /*[my_profile, appearance, balance, notifications, business_accounts, users]*/
  const { search, state: locationState } = useLocation()
  const { hash, action } = useParams()
  const navigate = useNavigate()

  const backPath = locationState?.from || (isAdmin ? "/users" : RoutesPaths.dashboard.root)

  const refs = {
    billing: useRef(null)
  }

  const params = new URLSearchParams(search)
  const user_id = params.get("user_id")

  const [denyUserReq, setDenyUserReq] = useState(false)

  const hasHighAccessRole = businessAccounts?.some(({ role }) =>
    highAccessRoles?.includes(role?.id)
  )

  // Team's gate mirrors the one the standalone /team route used: a user with no
  // business accounts yet is let through, so an empty list on first render
  // doesn't bounce /settings/team back to My profile.
  const canManageTeam = hasHighAccessRole || businessAccounts?.length === 0

  const isReferralEnabled = useIsReferralEnabled()
  const isBalanceTabAvailable =
    !userInfo?.is_loyalty_billing ||
    (userInfo?.is_loyalty_billing && bannerInfo?.balance?.amount >= 0)

  const actualTablist = isAdmin
    ? tabsListAdmin
    : getTabsList({
        referralEnabled: isReferralEnabled,
        isBalanceTabAvailable,
        teamEnabled: canManageTeam
      })

  const sections = SECTION_ORDER.map((name) => ({
    name,
    items: actualTablist.filter((t) => t.section === name)
  })).filter((s) => s.items.length)

  function handleNavigate(subPage) {
    navigate(`${RoutesPaths.settings.root}/${subPage}${search}`)
    if (isMobile) props.closeSettingsMenu()
  }

  const handleInitialNavigation = (hash) => {
    // Handle hash navigation: only allow known hashes, otherwise redirect to profile.
    if (hash) {
      const isValidRoute = actualTablist.map((tab) => tab.id)

      if (isValidRoute.includes(hash)) {
        if (action) {
          navigate(`${RoutesPaths.settings.root}/${hash}/${action}${search || ""}`, {
            replace: true
          })
          navigate(`${RoutesPaths.settings.root}/${hash}/${action}${search || ""}`, {
            replace: true,
            state: locationState
          })
          return
        } else {
          navigate(`${RoutesPaths.settings.root}/${hash}${search || ""}`, {
            replace: true,
            state: locationState
          })
          return
        }
      } else {
        navigate(isAdmin ? RoutesPaths.settings.notifications : RoutesPaths.settings.my_profile)
      }
    }
  }

  useEffect(() => {
    document.title = "Personal Settings - Chargeback"

    handleInitialNavigation(hash)
  }, [])

  useEffect(() => {
    if (!isAdmin) {
      if (hash === "my_profile" || saveResult?.confirmEmail) {
        props.getUserInfo()
      }
    } else if (user_id && isAdmin) {
      props.getUserInfoByAdmin({ user_id })
    }
  }, [hash, saveResult, isAdmin])

  useEffect(() => {
    if (hash === "balance") props.getBalance(user_id ? { user_id } : undefined)
  }, [hash])

  useBalanceTopUpEvent()

  const content = {
    my_profile: <MyProfile />,
    appearance: <Appearance />,
    balance: <Balance billingTabRef={refs["billing"]} withTitleBlock={true} />,
    billing: <Billing fullInfo={true} personalSetting={true} />,
    notifications: <Notifications user_id={user_id} />,
    business_accounts: <BusinessAccounts />,
    referrals: isReferralEnabled ? <Referrals /> : null,
    public_api: isAdmin ? (
      <PublicAPI tab={action} basePath={`${RoutesPaths.settings.root}/public_api`} />
    ) : null,
    mcp: (
      <>
        <TitleSection
          icon="color_palette"
          title="MCP"
          description="Connect AI assistants like Claude and ChatGPT via MCP."
          style={{ margin: isMobile ? "-16px -16px 16px" : "-32px -32px 24px" }}
        >
          <a
            className="title-section-tutorial-link"
            href={`${process.env.REACT_APP_BASE_URI}/mcp`}
            target="_blank"
            rel="noreferrer"
          >
            <Icon type="info_bubble" />
            {t("Learn more about MCP")}
          </a>
        </TitleSection>
        <McpIntegrations />
      </>
    ),
    users: hasHighAccessRole ? <Users /> : null,
    team: canManageTeam ? <TeamPage /> : null,
    developers: (
      <>
        <TitleSection
          icon="developer"
          title="Developers"
          description="Manage your API keys and webhooks"
          style={{ margin: isMobile ? "-16px -16px 16px" : "-32px -32px 24px" }}
        />
        <PublicAPI tab={action} />
      </>
    )
  }

  return (
    <div className={"settings-page"}>
      {isMobile && (
        <>
          <div
            className={cls("settings-mobile-sidebar__shadow", {
              active: isShowMenu
            })}
            onClick={props.closeSettingsMenu}
          />
          <div className={cls("settings-mobile-sidebar", { open: isShowMenu })}>
            <div className="settings-mobile-sidebar-top-section">
              <button
                className="settings-mobile-sidebar__back"
                onClick={() => {
                  navigate(RoutesPaths.dashboard.root)
                  props.closeSettingsMenu()
                }}
              >
                <Icon type="arrow_left2" />
                <span>{t("Back to app")}</span>
              </button>
              <button
                className="main-sidebar-header-btn mobile-close-btn"
                onClick={props.closeSettingsMenu}
                aria-label="Close menu"
              >
                <Icon type="mobile_side_bar" />
              </button>
            </div>

            <div className="settings-mobile-sidebar__groups">
              {sections.map((section) => (
                <div key={section.name} className="settings-mobile-sidebar__group">
                  <span
                    className="settings-mobile-sidebar__group-label"
                    data-testid={buildTestId("settings-section", section.name)}
                  >
                    {t(section.name)}
                  </span>
                  <div className="settings-mobile-sidebar__group-items">
                    {section.items.map((tab) => {
                      const isActive = hash === tab.id
                      return (
                        <button
                          key={tab.id}
                          className={cls("settings-mobile-sidebar__tab", {
                            active: isActive
                          })}
                          onClick={() => handleNavigate(tab.id)}
                          data-testid={`settings-tab-${tab.id}`}
                        >
                          <Icon type={tab.icon} isActive={isActive} />
                          <span>{t(tab.text)}</span>
                          {tab.hot && (
                            <span className="mark-hot">
                              <Icon type="sparkles_soft" />
                              <Text
                                style={{
                                  color: "currentColor",
                                  textTransform: "uppercase"
                                }}
                              >
                                {t("Hot")}
                              </Text>
                            </span>
                          )}
                          {tab.new && (
                            <span className="mark-new">
                              <Text
                                style={{
                                  color: "currentColor",
                                  textTransform: "uppercase"
                                }}
                              >
                                {t("New")}
                              </Text>
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            <button className="settings-mobile-sidebar__logout" onClick={props.logout}>
              <Icon type="logout" />
              <span>{t("Log out")}</span>
            </button>
          </div>
        </>
      )}
      <div className="sider">
        <Title level={3} style={{ textAlign: "left", marginBottom: 24 }}>
          <Flex align="center">
            <ButtonComponent
              type="text"
              icon={<Icon type="arrow_left2" />}
              style={{ width: 32 }}
              onClick={() => navigate(backPath)}
            />
            {t("Settings")}
          </Flex>
        </Title>

        {/* <Text
          type="secondary"
          strong
          style={{ display: "inline-block", marginBottom: 8 }}
        >
          {t("Account")}
        </Text> */}
        <Flex gap={24} vertical={true}>
          {sections.map((section) => (
            <Flex gap={8} vertical={true} key={section.name}>
              <Text
                type="secondary"
                className="settings-section-title"
                data-testid={buildTestId("settings-section", section.name)}
              >
                {t(section.name)}
              </Text>
              <Flex gap={2} vertical={true}>
                {section.items.map((tab) => {
                  const isUsersTab = tab.id === "users"
                  const isRestricted = isUsersTab && !hasHighAccessRole
                  const isActive = hash === tab.id

                  const button = (
                    <ButtonComponent
                      key={tab.id}
                      type="text"
                      size="large"
                      onClick={isRestricted ? null : () => handleNavigate(tab.id)}
                      className={cls("flex", { active: isActive })}
                      disabled={isRestricted}
                      style={{ width: "100%", borderRadius: 8 }}
                      icon={<Icon type={tab.icon} isActive={isActive} />}
                      data-testid={`settings-tab-${tab.id}`}
                    >
                      {t(tab.text)}
                      {tab?.hot ? (
                        <Flex gap={4} align="center" className="mark-hot">
                          <Icon type="sparkles_soft" />
                          <Text
                            style={{
                              color: "currentColor",
                              textTransform: "uppercase"
                            }}
                          >
                            {t("Hot")}
                          </Text>
                        </Flex>
                      ) : tab?.new ? (
                        <Flex gap={4} align="center" className="mark-new ">
                          <Text
                            style={{
                              color: "currentColor",
                              textTransform: "uppercase"
                            }}
                          >
                            {t("New")}
                          </Text>
                        </Flex>
                      ) : null}
                    </ButtonComponent>
                  )

                  return isRestricted ? (
                    <Tooltip key={tab.id} placement="bottom" title={tab.tooltip}>
                      <div style={{ width: "100%" }}>{button}</div>
                    </Tooltip>
                  ) : (
                    <div ref={refs[tab?.id]} key={tab?.id} style={{ width: "100%" }}>
                      {button}
                    </div>
                  )
                })}
              </Flex>
            </Flex>
          ))}
        </Flex>

        <ButtonComponent
          type="text"
          size="large"
          className="flex settings-logout"
          icon={<Icon type="logout" />}
          onClick={() => props.logout()}
          style={{ width: "100%", borderRadius: 8 }}
        >
          {t("Log out")}
        </ButtonComponent>
      </div>
      <div className={cls("content scroll-container", hash)}>{content[hash]}</div>
    </div>
  )
}

PersonalSettingsPage.defaultProps = {}

PersonalSettingsPage.propTypes = {
  fetching: PropTypes.bool,
  isMobile: PropTypes.bool,
  theme: PropTypes.string
}

const mapStateToProps = (state) => ({
  userInfo: state.auth.userInfo,
  isAdmin: state.auth.isAdmin,
  isMobile: state.nav.isMobile,
  isShowMenu: state.nav.isShowMenu,
  theme: state.nav.theme,
  businessAccounts: state.user.businessAccountsWithRole,
  balanceEnabled: state.auth.userInfo?.["balance_feature"],
  saveResult: state.user.saveResult,
  bannerInfo: state.user.bannerInfo
})

const mapDispatchToProps = (dispatch) => ({
  getUserInfo: () => dispatch(getUserInfo()),
  getUserInfoByAdmin: (params) => dispatch(getUserInfoByAdmin(params)),
  confirmEmail: (params) => dispatch(confirmEmails(params)),
  getBusinessAccount: (businessAccountId) => dispatch(getBusinessAccount(businessAccountId)),
  clearBusinessAccount: () => dispatch(userSetProp({ key: "businessAccount", value: null })),
  getBalance: (params) => dispatch(BalanceActions.getBalanceRequest(params)),
  closeSettingsMenu: () => dispatch(navActions.navSetProp({ key: "isShowMenu", value: false })),
  logout: () => dispatch(logoutThunk())
})

export default connect(mapStateToProps, mapDispatchToProps)(PersonalSettingsPage)
